import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { NavItem } from '../api/alpha';
import { useShell } from './ShellProvider';
import { shell } from './shellClasses';

function initials(label: string) {
  return label
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

function NavLinkRow({
  item,
  collapsed,
  onNavigate,
  trailing,
}: {
  item: NavItem;
  collapsed?: boolean;
  onNavigate?: () => void;
  trailing?: ReactNode;
}) {
  const location = useLocation();
  const active =
    item.path === '/command'
      ? location.pathname === '/command' || location.pathname === '/'
      : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);

  return (
    <li>
      <div className="group flex items-center gap-0.5">
        <Link
          to={item.path}
          onClick={onNavigate}
          aria-current={active ? 'page' : undefined}
          title={collapsed ? item.label : undefined}
          className={`flex min-h-[44px] flex-1 items-center gap-2 rounded-[var(--shell-radius-md)] px-3 text-sm font-medium transition-colors duration-[var(--shell-motion-fast)] ${shell.focusRing} ${
            active
              ? 'border-l-[3px] border-[var(--shell-accent)] bg-[var(--shell-accent-muted)] text-[var(--shell-accent)]'
              : 'border-l-[3px] border-transparent text-[var(--shell-fg-muted)] hover:bg-[var(--shell-hover)] hover:text-[var(--shell-fg)]'
          } ${collapsed ? 'justify-center px-2' : ''}`}
        >
          {collapsed ? (
            <span aria-hidden className="text-xs font-semibold">
              {initials(item.label)}
            </span>
          ) : (
            <span className="truncate">{item.label}</span>
          )}
          {collapsed && <span className="sr-only">{item.label}</span>}
        </Link>
        {!collapsed && trailing}
      </div>
    </li>
  );
}

export function ShellSidebar({ onNavigate }: { onNavigate?: () => void }) {
  const {
    primaryNav,
    collapsed,
    setCollapsed,
    mode,
    setMode,
    pinned,
    favorites,
    recent,
    togglePinned,
    toggleFavorite,
    allNav,
  } = useShell();

  const byPath = new Map(allNav.map((n) => [n.path, n]));
  for (const n of primaryNav) byPath.set(n.path, n);

  const resolveItems = (paths: string[]) =>
    paths
      .map((p) => byPath.get(p))
      .filter((x): x is NavItem => Boolean(x));

  const groups = primaryNav.reduce<Record<string, NavItem[]>>((acc, item) => {
    acc[item.group] = acc[item.group] ?? [];
    acc[item.group].push(item);
    return acc;
  }, {});

  const groupOrder = ['Home', 'Commerce', 'Control', 'Platform', 'Account'];

  const pinSet = new Set(pinned);
  const favSet = new Set(favorites);

  return (
    <nav aria-label="Main navigation" className={`flex h-full flex-col ${shell.sidebar}`}>
      <div
        className={`sticky top-0 z-10 flex items-start justify-between gap-2 border-b ${shell.border} bg-[var(--shell-sidebar)] p-3`}
      >
        <div className={collapsed ? 'sr-only' : 'min-w-0'}>
          <p className={`text-[10px] font-semibold uppercase tracking-wider ${shell.subtle}`}>
            Amazon Commerce
          </p>
          <p className="truncate text-lg font-semibold tracking-tight text-[var(--shell-fg)]">
            Founder OS
          </p>
        </div>
        {collapsed && (
          <p className="w-full text-center text-sm font-bold text-[var(--shell-accent)]" title="Founder OS">
            FO
          </p>
        )}
        <button
          type="button"
          className={`${shell.controlIcon} shrink-0`}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-pressed={collapsed}
          onClick={() => setCollapsed(!collapsed)}
        >
          <span aria-hidden className="text-xs">
            {collapsed ? '»' : '«'}
          </span>
        </button>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-3">
        {pinned.length > 0 && (
          <section aria-label="Pinned">
            {!collapsed && (
              <p className={`mb-1 px-2 text-[10px] font-semibold uppercase tracking-wide ${shell.subtle}`}>
                Pinned
              </p>
            )}
            <ul className="space-y-0.5">
              {resolveItems(pinned).map((item) => (
                <NavLinkRow key={`pin-${item.path}`} item={item} collapsed={collapsed} onNavigate={onNavigate} />
              ))}
            </ul>
          </section>
        )}

        {favorites.length > 0 && (
          <section aria-label="Favorites">
            {!collapsed && (
              <p className={`mb-1 px-2 text-[10px] font-semibold uppercase tracking-wide ${shell.subtle}`}>
                Favorites
              </p>
            )}
            <ul className="space-y-0.5">
              {resolveItems(favorites).map((item) => (
                <NavLinkRow key={`fav-${item.path}`} item={item} collapsed={collapsed} onNavigate={onNavigate} />
              ))}
            </ul>
          </section>
        )}

        {recent.length > 0 && mode === 'founder' && (
          <section aria-label="Recent">
            {!collapsed && (
              <p className={`mb-1 px-2 text-[10px] font-semibold uppercase tracking-wide ${shell.subtle}`}>
                Recent
              </p>
            )}
            <ul className="space-y-0.5">
              {resolveItems(recent.slice(0, 5)).map((item) => (
                <NavLinkRow key={`rec-${item.path}`} item={item} collapsed={collapsed} onNavigate={onNavigate} />
              ))}
            </ul>
          </section>
        )}

        {groupOrder
          .filter((g) => groups[g]?.length)
          .map((group) => (
            <section key={group} aria-label={group}>
              {!collapsed && (
                <p className={`mb-1 px-2 text-[10px] font-semibold uppercase tracking-wide ${shell.subtle}`}>
                  {group}
                </p>
              )}
              <ul className="space-y-0.5">
                {groups[group]
                  .slice()
                  .sort((a, b) => a.order - b.order)
                  .map((item) => (
                    <NavLinkRow
                      key={item.id}
                      item={item}
                      collapsed={collapsed}
                      onNavigate={onNavigate}
                      trailing={
                        mode === 'founder' ? (
                          <div className="flex opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                            <button
                              type="button"
                              className={`min-h-[44px] min-w-[36px] text-xs ${shell.subtle} ${shell.focusRing}`}
                              aria-label={pinSet.has(item.path) ? `Unpin ${item.label}` : `Pin ${item.label}`}
                              onClick={() => togglePinned(item.path)}
                            >
                              {pinSet.has(item.path) ? 'Unpin' : 'Pin'}
                            </button>
                            <button
                              type="button"
                              className={`min-h-[44px] min-w-[36px] text-xs ${shell.subtle} ${shell.focusRing}`}
                              aria-label={
                                favSet.has(item.path) ? `Unfavorite ${item.label}` : `Favorite ${item.label}`
                              }
                              onClick={() => toggleFavorite(item.path)}
                            >
                              {favSet.has(item.path) ? '★' : '☆'}
                            </button>
                          </div>
                        ) : null
                      }
                    />
                  ))}
              </ul>
            </section>
          ))}

        {/* Remaining engineer groups */}
        {mode === 'engineer' &&
          Object.entries(groups)
            .filter(([g]) => !groupOrder.includes(g))
            .map(([group, items]) => (
              <section key={group} aria-label={group}>
                {!collapsed && (
                  <p className={`mb-1 px-2 text-[10px] font-semibold uppercase tracking-wide ${shell.subtle}`}>
                    {group}
                  </p>
                )}
                <ul className="space-y-0.5">
                  {items
                    .slice()
                    .sort((a, b) => a.order - b.order)
                    .map((item) => (
                      <NavLinkRow
                        key={item.id}
                        item={item}
                        collapsed={collapsed}
                        onNavigate={onNavigate}
                      />
                    ))}
                </ul>
              </section>
            ))}
      </div>

      <div className={`border-t ${shell.border} p-3`}>
        <button
          type="button"
          className={`w-full ${shell.control} ${shell.focusRing} ${shell.hoverRow}`}
          aria-pressed={mode === 'engineer'}
          onClick={() => setMode(mode === 'founder' ? 'engineer' : 'founder')}
        >
          {collapsed
            ? mode === 'founder'
              ? 'Eng'
              : 'Fnd'
            : mode === 'founder'
              ? 'Switch to Engineer nav'
              : 'Switch to Founder nav'}
        </button>
      </div>
    </nav>
  );
}
